import { useLayoutEffect } from 'react';

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const SUBMIT = 'button[type="submit"]:not([disabled])';
const OVERLAY = '.checkout-wait-overlay';
const returnTargets = new WeakMap();

const focusIsInside = (host) => {
  const focused = document.activeElement;
  return Boolean(focused) && focused !== document.body && focused.isConnected && host.contains(focused);
};

const canReturnTo = (element) => Boolean(element?.isConnected) && !element.disabled;

const firstMatch = (elements, selectors) => {
  for (const selector of selectors) {
    const match = elements.map((element) => element.querySelector(selector)).find(Boolean);
    if (match) return match;
  }
  return null;
};

const useCoveredSiblings = (overlayRef) => {
  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    const host = overlay.parentElement;
    const active = document.activeElement;
    const returnTo = active && active !== document.body && !active.closest(OVERLAY) ? active : returnTargets.get(host);
    returnTargets.set(host, returnTo);

    const covered = [...host.children].filter((element) => (
      element !== overlay && !element.hasAttribute('inert')
    ));

    covered.forEach((element) => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });

    return () => {
      covered.forEach((element) => {
        element.removeAttribute('inert');
        element.removeAttribute('aria-hidden');
      });

      setTimeout(() => {
        if (!host.isConnected) return;
        if (host.querySelector(`:scope > ${OVERLAY}`)) return;
        if (focusIsInside(host)) return;

        returnTargets.delete(host);
        const target = canReturnTo(returnTo) ? returnTo : firstMatch(covered, [SUBMIT, FOCUSABLE]);
        target?.focus();
      }, 0);
    };
  }, [overlayRef]);
};

export default useCoveredSiblings;
